import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Box } from "@chakra-ui/react";

const animateNext = {
  initial: { x: "50%", opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: "-50%", opacity: 0 }
};

const animatePrevious = {
  initial: { x: "-50%", opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: "50%", opacity: 0 }
};

export default function Flow({ steps, initialStep, ...rest }) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [direction, setDirection] = useState("next");
  const [isAnimating, setIsAnimating] = useState(false);
  const [state, setState] = useState({});
  const [history, setHistory] = useState([initialStep]);

  const _steps = useRef(steps).current;
  const nextStepId = useRef(null);

  const Step = useMemo(
    () => _steps.find((step) => step.id === currentStep)?.Component,
    [_steps, currentStep]
  );
  const animationVariant = direction === "next" ? animateNext : animatePrevious;

  const updateState = useCallback((update = {}) => {
    setState((prev) => ({ ...prev, ...update }));
  }, []);
  const updateHistory = useCallback((update) => {
    setHistory((prev) => [update, ...prev]);
  }, []);
  const updateProvidingStore = useCallback(
    ({ state, history }) => {
      updateState(state);
      updateHistory(history);
    },
    [updateHistory, updateState]
  );

  const push = useCallback(
    (stepId, { stepDirection = "next", state: stateUpdate = {} } = {}) => {
      updateProvidingStore({
        state: stateUpdate,
        history: stepId
      });

      nextStepId.current = stepId;
      setDirection(stepDirection);
      setIsAnimating(true);
    },
    [updateProvidingStore]
  );

  const next = useCallback(
    ({ state: stateUpdate = {} }) => {
      const currentIndex = _steps.findIndex((step) => step.id === currentStep);
      const nextStep = _steps[currentIndex + 1];
      if (nextStep) {
        updateProvidingStore({
          state: stateUpdate,
          history: nextStep.id
        });

        nextStepId.current = nextStep.id;
        setDirection("next");
        setIsAnimating(true);
      }
    },
    [_steps, currentStep, updateProvidingStore]
  );

  const previous = useCallback(
    ({ state: stateUpdate = {} }) => {
      const currentIndex = _steps.findIndex((step) => step.id === currentStep);
      const previousStep = _steps[currentIndex - 1];
      if (previousStep) {
        updateProvidingStore({
          state: stateUpdate,
          history: previousStep.id
        });

        nextStepId.current = previousStep.id;
        setDirection("previous");
        setIsAnimating(true);
      }
    },
    [_steps, currentStep, updateProvidingStore]
  );

  const backToFirstStep = useCallback(
    ({ state: stateUpdate = {} }) => {
      updateProvidingStore({
        state: stateUpdate,
        history: initialStep
      });
      setCurrentStep(initialStep);
    },
    [initialStep, updateProvidingStore]
  );

  useEffect(() => {
    if (isAnimating && nextStepId.current) {
      setCurrentStep(nextStepId.current);
      setIsAnimating(false);
    }
  }, [isAnimating]);

  const flowProviding = useMemo(() => {
    return {
      push,
      next,
      previous,
      backToFirstStep,
      setState: updateState,
      state,
      history
    };
  }, [push, next, previous, backToFirstStep, updateState, state, history]);

  return (
    <Box position="relative" w="100%" h="100%" overflowX="hidden">
      <AnimatePresence initial={false}>
        <motion.div
          key={currentStep}
          variants={animationVariant}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.3 }}
          style={{
            width: "100%",
            height: "100%",
            position: "absolute"
          }}
        >
          {(Step && (
            <Step
              push={push}
              next={next}
              previous={previous}
              backToFirstStep={backToFirstStep}
              flow={flowProviding}
              {...rest}
            />
          )) ||
            null}
        </motion.div>
      </AnimatePresence>
    </Box>
  );
}
